import React, {
	CSSProperties,
	FC,
	MouseEvent as ReactMouseEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import styles from '../journey-explorer.module.scss';
import {
	InteractionFilterMode,
	JourneyExploreResult,
	JourneyFilterState,
	JourneyMeta,
	TransitionFilterMode,
} from '../../../../models/journey-explorer/journey-explorer';
import { COLORS, MapMode, formatCompact, formatDuration, goalOf } from '../journey-explorer-utils';
import { isGroupLabel, isSimplified, simplifyOf, viewName } from '../journey-simplify';
import ExportCustomers from './export-customers';
import {
	DEFAULT_VIEW_BOX,
	InteractionInfo,
	MapEdge,
	MapNode,
	Point,
	ViewBox,
	borderWidth,
	BOX_WIDTH,
	DEFAULT_ASPECT,
	defaultPositions,
	edgeKey,
	fitViewBox,
	interactionMeasure,
	interactionSecs,
	isInteractionFiltered,
	isTransitionFiltered,
	journeyStarts,
	lineWidth,
	resolveInfo,
	routeEdge,
	transitionMeasure,
	transitionSecs,
	visibleInteractions,
	visibleTransitions,
	zoomViewBox,
} from '../journey-map-layout';
import MapInteraction from './map-interaction';
import MapTransition, { ARROW_ID } from './map-transition';
import MapPopup, { ExploreAction, PopupOption } from './map-popup';
import { ExplainContext, explainInteraction, explainTransition } from '../journey-explain';
import MapKey from './map-key';
import { DrawerView } from './explore-drawer';
import { LoadingPill } from './loading-indicator';
import { CompareData, interactionPair, transitionPair } from '../journey-compare';

const NO_CUSTOMERS = 'No customers match these filters. Remove a filter or widen a date range.';
const CLICK_TOLERANCE = 3;
const WHEEL_ZOOM_IN = 0.88;
const WHEEL_ZOOM_OUT = 1.14;
const BUTTON_ZOOM_IN = 0.8;
const BUTTON_ZOOM_OUT = 1.25;
const POPUP_WIDTH = 370;
const POPUP_HEIGHT = 330;
const EXPLAIN_HEIGHT = 470;
const POPUP_MARGIN = 8;

const INTERACTION_OPTIONS: PopupOption<InteractionFilterMode>[] = [
	{ mode: 'passed_through', label: 'Passed through this interaction' },
	{ mode: 'did_not_reach', label: 'Did not reach this interaction' },
	{ mode: 'started_here', label: 'Started their journey here' },
	{ mode: 'ended_here', label: 'Ended their journey here' },
];

const TRANSITION_OPTIONS: PopupOption<TransitionFilterMode>[] = [
	{ mode: 'went_through', label: 'Went through this transition' },
	{ mode: 'did_not_go_through', label: 'Did not go through this transition' },
];

const INTERACTION_ACTIONS: PopupOption<ExploreAction>[] = [
	{ mode: 'breakdown', label: 'Break down by an attribute' },
	{ mode: 'customers', label: 'See customers' },
];

const TRANSITION_ACTIONS: PopupOption<ExploreAction>[] = [
	{ mode: 'breakdown', label: 'Break down by an attribute' },
	{ mode: 'time', label: 'Time between these steps' },
	{ mode: 'customers', label: 'See customers' },
];

type Popup = { kind: 'interaction'; name: string } | { kind: 'transition'; key: string };

type Drag =
	| { kind: 'pan'; clientX: number; clientY: number; origin: ViewBox }
	| { kind: 'move'; name: string; clientX: number; clientY: number; origin: Point; pointer: Point };

interface JourneyMapProps {
	meta: JourneyMeta;
	lookup: Map<string, InteractionInfo>;
	result: JourneyExploreResult | null;
	filterState: JourneyFilterState;
	mode: MapMode;
	interactionsPct: number;
	transitionsPct: number;
	loading: boolean;
	onInteractionFilter: (name: string, mode: InteractionFilterMode) => void;
	onTransitionFilter: (from: string, to: string, mode: TransitionFilterMode) => void;
	onExplore: (view: DrawerView) => void;
	onExpandGroup?: (label: string) => void;
	compare: CompareData | null;
}

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(value, max));

const JourneyMap: FC<JourneyMapProps> = ({
	meta,
	lookup,
	result,
	filterState,
	mode,
	interactionsPct,
	transitionsPct,
	loading,
	onInteractionFilter,
	onTransitionFilter,
	onExplore,
	onExpandGroup,
	compare,
}) => {
	const simplify = simplifyOf(filterState);
	const goalName = goalOf(filterState);
	const goalLabel = goalName ? resolveInfo(lookup, viewName(simplify, goalName) ?? goalName).label : null;
	const containerRef = useRef<HTMLDivElement>(null);
	const svgRef = useRef<SVGSVGElement>(null);
	const movedRef = useRef(false);
	const [size, setSize] = useState({ w: DEFAULT_VIEW_BOX.w, h: DEFAULT_VIEW_BOX.h });
	const [viewBox, setViewBox] = useState<ViewBox>(DEFAULT_VIEW_BOX);
	const [moved, setMoved] = useState<Record<string, Point>>({});
	const [drag, setDrag] = useState<Drag | null>(null);
	const [hovered, setHovered] = useState<string | null>(null);
	const [expanded, setExpanded] = useState(false);
	const [popup, setPopup] = useState<Popup | null>(null);

	const toSvgPoint = useCallback((clientX: number, clientY: number): Point => {
		const svg = svgRef.current;
		const matrix = svg?.getScreenCTM();
		if (!svg || !matrix) {
			return { x: 0, y: 0 };
		}
		const point = svg.createSVGPoint();
		point.x = clientX;
		point.y = clientY;
		const { x, y } = point.matrixTransform(matrix.inverse());
		return { x, y };
	}, []);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}
		const observer = new ResizeObserver(([entry]) =>
			setSize({ w: entry.contentRect.width, h: entry.contentRect.height })
		);
		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const svg = svgRef.current;
		if (!svg) {
			return;
		}
		const onWheel = (event: WheelEvent) => {
			event.preventDefault();
			const center = toSvgPoint(event.clientX, event.clientY);
			setViewBox((current) => zoomViewBox(current, event.deltaY < 0 ? WHEEL_ZOOM_IN : WHEEL_ZOOM_OUT, center));
		};
		svg.addEventListener('wheel', onWheel, { passive: false });
		return () => svg.removeEventListener('wheel', onWheel);
	}, [toSvgPoint]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setPopup(null);
				setExpanded(false);
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, []);

	useEffect(() => {
		if (!drag) {
			return;
		}
		const onMove = (event: MouseEvent) => {
			const dx = event.clientX - drag.clientX;
			const dy = event.clientY - drag.clientY;
			if (Math.abs(dx) + Math.abs(dy) > CLICK_TOLERANCE) {
				movedRef.current = true;
			}
			if (!movedRef.current) {
				return;
			}
			if (drag.kind === 'pan') {
				const scale = Math.min(size.w / drag.origin.w, size.h / drag.origin.h);
				setViewBox({ ...drag.origin, x: drag.origin.x - dx / scale, y: drag.origin.y - dy / scale });
				return;
			}
			const pointer = toSvgPoint(event.clientX, event.clientY);
			setMoved((current) => ({
				...current,
				[drag.name]: {
					x: drag.origin.x + pointer.x - drag.pointer.x,
					y: drag.origin.y + pointer.y - drag.pointer.y,
				},
			}));
		};
		const onUp = () => setDrag(null);
		window.addEventListener('mousemove', onMove);
		window.addEventListener('mouseup', onUp);
		return () => {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
		};
	}, [drag, size, toSvgPoint]);

	const typeColors = useMemo(
		() => new Map(meta.interaction_types.map((type): [string, string] => [type.type, type.color])),
		[meta]
	);

	const aspect = size.w > 0 && size.h > 0 ? Math.round((size.w / size.h) * 4) / 4 : DEFAULT_ASPECT;

	const layoutRef = useRef<{ meta: JourneyMeta; key: string; positions: Map<string, Point> } | null>(null);
	const needsFitRef = useRef(false);
	const basePositions = useMemo(() => {
		const cached = layoutRef.current;
		if (!result?.interactions.length) {
			return cached?.meta === meta ? cached.positions : new Map<string, Point>();
		}
		const names = visibleInteractions(result.interactions, interactionsPct).map((item) => item.name);
		const key = `${aspect}\u0000${[...names].sort().join('\u0000')}`;
		if (cached && cached.meta === meta && cached.key === key) {
			return cached.positions;
		}
		const positions = defaultPositions(meta, names, result.transitions, journeyStarts(result.paths), aspect);
		layoutRef.current = { meta, key, positions };
		needsFitRef.current = true;
		return positions;
	}, [meta, result, interactionsPct, aspect]);

	const nodes = useMemo<MapNode[]>(() => {
		if (!result) {
			return [];
		}
		return visibleInteractions(result.interactions, interactionsPct).map((stat) => {
			const info = resolveInfo(lookup, stat.name, stat.type, typeColors.get(stat.type));
			const position = moved[stat.name] ?? basePositions.get(stat.name) ?? { x: 0, y: 0 };
			return { ...position, info, width: BOX_WIDTH, stat };
		});
	}, [result, interactionsPct, lookup, typeColors, moved, basePositions]);

	const nodeByName = useMemo(() => new Map(nodes.map((node): [string, MapNode] => [node.info.name, node])), [nodes]);

	const colours = useMemo(() => {
		const shown = new Set(nodes.map((node) => node.info.color));
		return meta.interaction_types
			.filter((type) => shown.has(type.color))
			.map((type) => ({ label: type.label, color: type.color }));
	}, [meta, nodes]);

	const edges = useMemo<MapEdge[]>(
		() =>
			visibleTransitions(result ? result.transitions : [], new Set(nodeByName.keys()), transitionsPct).map(
				(stat) => {
					const from = nodeByName.get(stat.from) as MapNode;
					const to = nodeByName.get(stat.to) as MapNode;
					return { key: edgeKey(stat.from, stat.to), from, to, stat, ...routeEdge(from, to, nodes) };
				}
			),
		[result, nodes, nodeByName, transitionsPct]
	);

	useEffect(() => {
		if (!needsFitRef.current || !nodes.length) {
			return;
		}
		needsFitRef.current = false;
		const next = fitViewBox(nodes, edges);
		if (next) {
			setViewBox(next);
		}
	}, [nodes, edges]);

	const focus = drag ? null : hovered;
	const neighbours = useMemo(() => {
		const names = new Set<string>();
		if (focus) {
			names.add(focus);
			edges.forEach((edge) => {
				if (edge.from.info.name === focus) {
					names.add(edge.to.info.name);
				} else if (edge.to.info.name === focus) {
					names.add(edge.from.info.name);
				}
			});
		}
		return names;
	}, [edges, focus]);

	const maxInteraction = Math.max(0, ...nodes.map((node) => interactionMeasure(node.stat, mode)));
	const maxTransition = Math.max(0, ...edges.map((edge) => transitionMeasure(edge.stat, mode)));

	const handlePress = useCallback(
		(node: MapNode, event: ReactMouseEvent<SVGGElement>) => {
			movedRef.current = false;
			setDrag({
				kind: 'move',
				name: node.info.name,
				clientX: event.clientX,
				clientY: event.clientY,
				origin: { x: node.x, y: node.y },
				pointer: toSvgPoint(event.clientX, event.clientY),
			});
		},
		[toSvgPoint]
	);

	const handleSelectNode = useCallback((node: MapNode) => {
		if (movedRef.current) {
			movedRef.current = false;
			return;
		}
		const name = node.info.name;
		setPopup((current) =>
			current?.kind === 'interaction' && current.name === name ? null : { kind: 'interaction', name }
		);
	}, []);

	const handleSelectEdge = useCallback(
		(edge: MapEdge) =>
			setPopup((current) =>
				current?.kind === 'transition' && current.key === edge.key
					? null
					: { kind: 'transition', key: edge.key }
			),
		[]
	);

	const handleHover = useCallback((name: string | null) => setHovered(name), []);

	const handleCanvasPress = (event: ReactMouseEvent<SVGSVGElement>) => {
		if (event.button !== 0) {
			return;
		}
		movedRef.current = false;
		setDrag({ kind: 'pan', clientX: event.clientX, clientY: event.clientY, origin: viewBox });
	};

	const handleCanvasClick = () => {
		if (movedRef.current) {
			movedRef.current = false;
			return;
		}
		setPopup(null);
	};

	const zoomFromCentre = (factor: number) =>
		setViewBox((current) =>
			zoomViewBox(current, factor, { x: current.x + current.w / 2, y: current.y + current.h / 2 })
		);

	const fit = () => {
		const next = fitViewBox(nodes, edges);
		if (next) {
			setViewBox(next);
		}
	};

	const scale = Math.min(size.w / viewBox.w, size.h / viewBox.h) || 1;
	const toScreen = (point: Point, height = POPUP_HEIGHT): CSSProperties => ({
		left: clamp(
			(size.w - viewBox.w * scale) / 2 + (point.x - viewBox.x) * scale,
			POPUP_MARGIN,
			size.w - POPUP_WIDTH
		),
		top: clamp((size.h - viewBox.h * scale) / 2 + (point.y - viewBox.y) * scale, POPUP_MARGIN, size.h - height),
	});

	const popupNode = popup?.kind === 'interaction' ? nodeByName.get(popup.name) : undefined;
	const popupEdge = popup?.kind === 'transition' ? edges.find((edge) => edge.key === popup.key) : undefined;
	const explainContext: ExplainContext | null = result
		? { kpis: result.kpis, goalLabel, label: (name) => resolveInfo(lookup, name).label }
		: null;
	const isEmpty = result !== null && result.kpis.customers === 0;
	const fading = loading && result !== null;

	return (
		<div ref={containerRef} className={`${styles.canvas} ${expanded ? styles.canvas_expanded : ''}`}>
			<svg
				ref={svgRef}
				className={`${styles.map_svg} ${drag?.kind === 'pan' ? styles.map_svg_panning : ''} ${
					fading ? styles.loading : ''
				}`}
				viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
				preserveAspectRatio="xMidYMid meet"
				onMouseDown={handleCanvasPress}
				onClick={handleCanvasClick}
			>
				<defs>
					<marker
						id={ARROW_ID}
						viewBox="0 0 10 10"
						refX="9"
						refY="5"
						markerUnits="userSpaceOnUse"
						markerWidth="7"
						markerHeight="7"
						orient="auto-start-reverse"
					>
						<path d="M0 0L10 5L0 10z" fill={COLORS.muted} />
					</marker>
				</defs>
				{edges.map((edge) => {
					const touchesFocus =
						focus !== null && (edge.from.info.name === focus || edge.to.info.name === focus);
					const [compareA, compareB] = compare
						? transitionPair(compare, edge.from.info.name, edge.to.info.name)
						: [undefined, undefined];
					return (
						<MapTransition
							key={edge.key}
							edge={edge}
							label={
								mode === 'customers'
									? formatCompact(edge.stat.customers)
									: formatDuration(transitionSecs(edge.stat, mode))
							}
							showGoal={goalLabel !== null}
							width={lineWidth(transitionMeasure(edge.stat, mode), maxTransition, mode)}
							selected={popup?.kind === 'transition' && popup.key === edge.key}
							filtered={isTransitionFiltered(filterState, edge.from.info.name, edge.to.info.name)}
							focused={touchesFocus}
							faded={focus !== null && !touchesFocus}
							compareA={compareA}
							compareB={compareB}
							onSelect={handleSelectEdge}
						/>
					);
				})}
				{nodes.map((node) => (
					<MapInteraction
						key={node.info.name}
						node={node}
						compareA={compare ? interactionPair(compare, node.info.name)[0] : undefined}
						compareB={compare ? interactionPair(compare, node.info.name)[1] : undefined}
						label={
							mode === 'customers'
								? formatCompact(node.stat.customers)
								: formatDuration(interactionSecs(node.stat, mode))
						}
						showGoal={goalLabel !== null}
						border={borderWidth(interactionMeasure(node.stat, mode), maxInteraction, mode)}
						highlighted={
							(popup?.kind === 'interaction' && popup.name === node.info.name) ||
							isInteractionFiltered(filterState, node.info.name)
						}
						faded={focus !== null && !neighbours.has(node.info.name)}
						dragging={drag?.kind === 'move' && drag.name === node.info.name}
						onPress={handlePress}
						onSelect={handleSelectNode}
						onHover={handleHover}
					/>
				))}
			</svg>
			{isEmpty && <div className={`${styles.empty_state} ${fading ? styles.loading : ''}`}>{NO_CUSTOMERS}</div>}
			<LoadingPill
				active={loading}
				label={compare ? 'Comparing segments' : 'Updating map'}
				className={styles.canvas_status}
			/>
			<div className={styles.canvas_header} onMouseDown={(event) => event.stopPropagation()}>
				<div className={styles.zoom_controls}>
					<button
						type="button"
						className={styles.zoom_button}
						title="Zoom in"
						onClick={() => zoomFromCentre(BUTTON_ZOOM_IN)}
					>
						+
					</button>
					<button
						type="button"
						className={styles.zoom_button}
						title="Zoom out"
						onClick={() => zoomFromCentre(BUTTON_ZOOM_OUT)}
					>
						−
					</button>
					<button type="button" className={styles.zoom_button} title="Fit" onClick={fit}>
						Fit
					</button>
				</div>
				<button
					type="button"
					className={styles.expand_button}
					aria-pressed={expanded}
					title={expanded ? 'Exit full screen (Esc)' : 'Full screen'}
					onClick={() => setExpanded((value) => !value)}
				>
					<svg
						width="12"
						height="12"
						viewBox="0 0 12 12"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						{expanded ? (
							<path d="M4.5 1v3.5H1M7.5 1v3.5H11M4.5 11V7.5H1M7.5 11V7.5H11" />
						) : (
							<path d="M1 4.5V1h3.5M11 4.5V1H7.5M1 7.5V11h3.5M11 7.5V11H7.5" />
						)}
					</svg>
					{expanded ? 'Exit' : 'Expand'}
				</button>
			</div>
			<MapKey
				mode={mode}
				goalLabel={goalLabel}
				overallGoalPct={result ? result.kpis.goal_rate_pct_unfiltered : null}
				colours={colours}
			/>
			{popupNode && (
				<MapPopup
					key={popupNode.info.name}
					explain={
						explainContext
							? () =>
									explainInteraction(
										popupNode.stat,
										result?.transitions.filter((t) => t.to === popupNode.info.name) ?? [],
										result?.transitions.filter((t) => t.from === popupNode.info.name) ?? [],
										explainContext
									)
							: undefined
					}
					title={popupNode.info.label}
					color={popupNode.info.color}
					goalPct={goalLabel ? popupNode.stat.reached_pct : null}
					options={isGroupLabel(simplify, popupNode.info.name) ? [] : INTERACTION_OPTIONS}
					actions={INTERACTION_ACTIONS}
					position={toScreen({ x: popupNode.x + popupNode.width + 8, y: popupNode.y })}
					explainPosition={toScreen({ x: popupNode.x + popupNode.width + 8, y: popupNode.y }, EXPLAIN_HEIGHT)}
					onChoose={(choice) => {
						setPopup(null);
						onInteractionFilter(popupNode.info.name, choice);
					}}
					onExplore={(action) => {
						setPopup(null);
						onExplore({
							mode: action,
							title: popupNode.info.label,
							segment: { kind: 'interaction', interaction: popupNode.info.name },
						});
					}}
					footer={
						<>
							{onExpandGroup && isGroupLabel(simplify, popupNode.info.name) && (
								<button
									type="button"
									className={`${styles.popup_option} ${styles.popup_explore}`}
									onClick={() => {
										setPopup(null);
										onExpandGroup(popupNode.info.name);
									}}
								>
									Show the steps inside
									<span aria-hidden="true">›</span>
								</button>
							)}
							<ExportCustomers
								filters={filterState}
								segment={{ kind: 'interaction', interaction: popupNode.info.name }}
								customers={popupNode.stat.customers}
								compact
							/>
						</>
					}
				/>
			)}
			{popupEdge && (
				<MapPopup
					key={popupEdge.key}
					explain={
						explainContext
							? () => explainTransition(popupEdge.stat, popupEdge.from.stat, explainContext)
							: undefined
					}
					title={`${popupEdge.from.info.label} to ${popupEdge.to.info.label}`}
					goalPct={goalLabel ? popupEdge.stat.reached_pct : null}
					options={isSimplified(simplify) ? [] : TRANSITION_OPTIONS}
					actions={TRANSITION_ACTIONS}
					position={toScreen({ x: popupEdge.label.x, y: popupEdge.label.y + 14 })}
					explainPosition={toScreen({ x: popupEdge.label.x, y: popupEdge.label.y + 14 }, EXPLAIN_HEIGHT)}
					onChoose={(choice) => {
						setPopup(null);
						onTransitionFilter(popupEdge.from.info.name, popupEdge.to.info.name, choice);
					}}
					onExplore={(action) => {
						setPopup(null);
						onExplore({
							mode: action,
							title: `${popupEdge.from.info.label} to ${popupEdge.to.info.label}`,
							segment: { kind: 'transition', from: popupEdge.from.info.name, to: popupEdge.to.info.name },
						});
					}}
					footer={
						<ExportCustomers
							filters={filterState}
							segment={{ kind: 'transition', from: popupEdge.from.info.name, to: popupEdge.to.info.name }}
							customers={popupEdge.stat.customers}
							compact
						/>
					}
				/>
			)}
		</div>
	);
};

export default JourneyMap;
